import * as Dialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  // Override del ancho desktop (clases Tailwind). Si se pasa, ignora `size`
  // para el ancho — para un modal puntual más ancho sin tocar el resto.
  widthClass?: string;
  // Si true, click fuera del modal NO lo cierra. Util para modales con
  // formularios largos donde un click accidental pierde trabajo.
  closeOnOutsideClick?: boolean;
  // Botón «Ayuda» de la ventana (`<AyudaVista>`), junto al título. Solo los
  // modales de trabajo grandes lo llevan; los chicos explican en línea.
  ayuda?: ReactNode;
};

// Ancho en DESKTOP (sm+). En mobile el modal es un bottom-sheet a ancho
// completo sin importar el size.
//
// **Los modales de trabajo agarran ~80% o más de la ventana. Está en piedra**
// (Joe, 2026-08-18: «necesito los modales grandes we, no chicos, que agarren el
// 80% al menos del tamaño del navegador»). Antes `lg` era `max-w-2xl` = 672 px
// FIJOS: en un monitor de 2,000 px eso es el 34% y se ve un recuadro perdido en
// medio de la pantalla, con el texto amontonado en una columna angosta mientras
// sobra espacio por todos lados.
//
// `sm` es la única excepción, y es a propósito: son confirmaciones de dos
// botones. Una pregunta de un renglón ocupando la pantalla completa no se lee
// como importante, se lee como error de maquetación.
//
// Los topes en píxeles son un freno para el ultrawide, no un límite de diseño:
// se subieron el 2026-08-19 porque en un monitor de 1,920 el tope de 1,700
// dejaba el modal en 88% y Joe lo cazó («¿por qué no usa todo el ancho?»). Con
// `xl` en 96vw/2200 un 1,920 queda en 1,843 px de modal. Si una vista se siente
// con la línea muy larga, la solución es repartir el ancho en columnas —no
// encoger la ventana.
const SIZE: Record<NonNullable<Props["size"]>, string> = {
  sm: "sm:max-w-lg",
  md: "sm:w-[80vw] sm:max-w-[1100px]",
  lg: "sm:w-[90vw] sm:max-w-[1700px]",
  xl: "sm:w-[96vw] sm:max-w-[2200px]",
};

export default function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
  widthClass,
  closeOnOutsideClick,
  ayuda,
}: Props) {
  // Alto: **se adapta**, no se fuerza. La primera versión clavaba los modales
  // grandes en `h-[88vh]` y eso resolvía un problema creando el contrario: una
  // lista de 40 casos se veía completa, pero un ZIP con UN archivo dejaba media
  // ventana vacía. Con `max-h` el modal crece hasta el tope cuando hay
  // contenido y se encoge cuando no — y las listas de adentro, que piden
  // `flex-1`, se estiran solas hasta ese tope. Dos quejas de Joe, una sola
  // regla: «no cortes» y «no desperdicies espacio» no se pelean.
  const llenaAlto = false;
  const anchoDesktop = widthClass ?? `sm:w-[92vw] ${SIZE[size]}`;
  // TODOS los modales por default NO se cierran con click outside —
  // proteccion contra clicks accidentales que pierden el formulario.
  // ESC y el boton Cancelar del footer siguen funcionando.
  const preventOutside = closeOnOutsideClick !== true;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm
                                   data-[state=open]:animate-in data-[state=closed]:animate-out
                                   data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          onPointerDownOutside={preventOutside ? (e) => e.preventDefault() : undefined}
          onInteractOutside={preventOutside ? (e) => e.preventDefault() : undefined}
          className={`fixed z-50 flex flex-col bg-ink-900 shadow-2xl
                      inset-x-0 bottom-0 w-full max-h-[92dvh] rounded-t-2xl border-t border-ink-700
                      px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]
                      sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 ${anchoDesktop}
                      sm:max-h-[90vh] sm:-translate-x-1/2 sm:-translate-y-1/2
                      sm:rounded-xl sm:border sm:px-5 sm:pt-5 sm:pb-5
                      ${llenaAlto ? "sm:h-[88vh]" : ""}
                      ${size === "lg" || size === "xl" ? "sm:min-h-[24rem]" : ""}
                      data-[state=open]:animate-in data-[state=closed]:animate-out
                      data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0
                      data-[state=open]:slide-in-from-bottom-8 data-[state=closed]:slide-out-to-bottom-8
                      sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0
                      sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95`}
        >
          {/* Agarradera (solo mobile) */}
          <div className="mx-auto mb-2 h-1 w-10 shrink-0 rounded-full bg-ink-600 sm:hidden" />
          <div className="shrink-0 pr-10 sm:pr-8">
          {/* Salida visible. Antes solo se podía cerrar con Escape o con el
              botón del pie, y un modal sin salida a la vista se siente
              atrapado — lo reportó Joe usando «Revisar mi correo». Va aquí,
              en `ui/Modal`, para que TODOS los modales la tengan y no haya
              que acordarse en cada uno. */}
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Cerrar"
                className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center
                           rounded text-zinc-400 transition hover:bg-ink-800 hover:text-zinc-100
                           focus-visible:outline-none focus-visible:ring-2
                           focus-visible:ring-accent-500 sm:h-8 sm:w-8"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
                     strokeLinecap="round" className="h-5 w-5" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </Dialog.Close>
            {ayuda ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <Dialog.Title className="text-lg font-semibold text-zinc-100">{title}</Dialog.Title>
                {ayuda}
              </div>
            ) : (
              <Dialog.Title className="text-lg font-semibold text-zinc-100">{title}</Dialog.Title>
            )}
            {description && (
              <Dialog.Description className="mt-1 text-sm text-zinc-400">
                {description}
              </Dialog.Description>
            )}
          </div>
          {/* Columna flex, no bloque. `h-full` (100%) NO sirve aquí: dentro de un
              contenedor con `max-height` el navegador trata ese alto como
              indefinido, el porcentaje se cae a `auto` y el hijo crece — el
              modal entero termina con una barra de scroll en vez de que
              scrollee la columna de adentro (lo cazó Joe: «creció hasta abajo,
              pero no dentro de la columna»). Con flex el hijo pide `flex-1` y
              queda acotado sin porcentajes. */}
          <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-y-auto scroll-sutil sm:mt-4">
            {children}
          </div>
          {footer && (
            <div className="mt-3 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-ink-700 pt-3 sm:mt-4 sm:pt-4">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
